import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dumbbell,
  ArrowRight,
  Menu,
  X,
  CheckCircle2,
  Users,
  Zap,
  QrCode,
  ShieldCheck,
  Smartphone,
  Bot,
  Sparkles,
  Mail,
  Phone
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { ModuleCarousel } from '@/components/ModuleCarousel';
import { WhatsAppWidget } from '@/components/WhatsAppWidget';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const releases = [
  {
    version: 'v2.5.0',
    date: '2026-06-01',
    title: 'Vencimiento visible en Check-in',
    changes: [
      { type: 'feature', text: 'Los socios ahora ven la fecha exacta de vencimiento de su membresia al hacer check-in.' },
      { type: 'improvement', text: 'Alertas visuales mejoradas: colores segun urgencia (rojo vencida, naranja por vencer).' },
    ]
  },
  {
    version: 'v2.4.0',
    date: '2026-05-15',
    title: 'Panel de Ventas con Buscador',
    changes: [
      { type: 'feature', text: 'Buscador rapido de clientes por nombre en el modulo de ventas.' },
      { type: 'improvement', text: 'Nuevo timeline de novedades en la landing page.' },
    ]
  },
  {
    version: 'v2.3.0',
    date: '2026-04-28',
    title: 'Reportes y Exportacion',
    changes: [
      { type: 'feature', text: 'Exportacion de datos a Excel para clientes, membresias, ventas y check-ins.' },
      { type: 'feature', text: 'Dashboard con metricas de crecimiento mensual y graficos interactivos.' },
      { type: 'fix', text: 'Correccion en calculo de membresias activas para gimnasios con multiples sucursales.' },
    ]
  },
  {
    version: 'v2.2.0',
    date: '2026-04-10',
    title: 'Control de Caja Chica',
    changes: [
      { type: 'feature', text: 'Sistema de caja chica: apertura, cierre, ingresos y egresos por sesion.' },
      { type: 'improvement', text: 'Integracion automatica de ventas en efectivo con la caja activa.' },
    ]
  },
];

const Index = () => {
  const navigate = useNavigate();
  const { user, gym } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === 'super_admin' && !gym) {
        navigate('/gyms');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, gym, navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Inicio', href: '#' },
    { label: 'Características', href: '#features' },
    { label: 'Novedades', href: '#releases' },
    { label: 'Nosotros', href: '#about' },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-orange-100 selection:text-orange-900 overflow-x-hidden">
      {/* Navigation */}
      <nav className={cn(
        "fixed top-0 w-full z-50 transition-all duration-300 border-b",
        scrolled
          ? "bg-white/95 backdrop-blur-md border-slate-200 py-3 shadow-sm"
          : "bg-white/40 backdrop-blur-md border-transparent py-5"
      )}>
        <div className="container mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-600 transition-transform group-hover:scale-110 shadow-lg shadow-orange-600/20">
              <Dumbbell className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">GymPro <span className="text-orange-600">SV</span></span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={cn(
                  "text-sm font-bold transition-colors hover:text-orange-600",
                  scrolled ? "text-slate-600" : "text-slate-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]"
                )}
              >
                {link.label}
              </a>
            ))}
            <Popover open={isSupportOpen} onOpenChange={setIsSupportOpen}>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    "text-sm font-bold transition-colors hover:text-orange-600",
                    scrolled ? "text-slate-600" : "text-slate-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]"
                  )}
                >
                  Soporte
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="end">
                <a
                  href="https://wa.me/50379557231"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-slate-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                  onClick={() => setIsSupportOpen(false)}
                >
                  <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                    <Phone className="h-4 w-4 text-green-600" />
                  </div>
                  WhatsApp
                </a>
                <a
                  href="mailto:gymprosv@gmail.com"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-slate-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                  onClick={() => setIsSupportOpen(false)}
                >
                  <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                    <Mail className="h-4 w-4 text-orange-600" />
                  </div>
                  Email
                </a>
              </PopoverContent>
            </Popover>
            <Button
              size="sm"
              variant="default"
              className="bg-slate-900 hover:bg-slate-800 text-white px-6 rounded-full"
              onClick={() => navigate('/login')}
            >
              Acceso Staff
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Sidebar - Moved outside of <nav> for solid background and correct z-index */}
      <div className={cn(
        "fixed inset-0 bg-white z-[60] transition-transform duration-300 md:hidden",
        isMenuOpen ? "translate-x-0" : "translate-x-full"
      )}>
        {/* Header inside Menu for Closing */}
        <div className="flex items-center justify-between px-6 py-5 border-b bg-white">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-600 shadow-lg shadow-orange-600/20">
              <Dumbbell className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">GymPro <span className="text-orange-600">SV</span></span>
          </div>
          <button className="p-2 text-slate-900" onClick={() => setIsMenuOpen(false)}>
            <X className="h-8 w-8" />
          </button>
        </div>

        <div className="flex flex-col p-8 gap-8 h-full bg-white relative overflow-y-auto">
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-orange-600/5 blur-[100px] rounded-full -z-10" />
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setIsMenuOpen(false)}
              className="text-3xl font-black text-slate-900 tracking-tighter"
            >
              {link.label}
            </a>
          ))}
          <div className="space-y-4">
            <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Soporte</p>
            <a
              href="https://wa.me/50379557231"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 text-2xl font-black text-green-600 tracking-tighter"
            >
              <Phone className="h-6 w-6" />
              WhatsApp
            </a>
            <a
              href="mailto:gymprosv@gmail.com"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 text-2xl font-black text-orange-600 tracking-tighter"
            >
              <Mail className="h-6 w-6" />
              Email
            </a>
          </div>
          <hr className="border-slate-100" />
          <Button
            size="lg"
            variant="default"
            className="bg-orange-600 hover:bg-orange-700 text-white w-full h-16 rounded-2xl text-xl font-bold"
            onClick={() => {
              setIsMenuOpen(false);
              navigate('/login');
            }}
          >
            Acceso Staff
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/hero-vibrant.png"
            alt="Fitness Energy"
            className="w-full h-full object-cover lg:object-right"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-transparent md:via-white/80 lg:via-white/50 z-[1]" />
        </div>

        <div className="container mx-auto px-6 relative z-10 w-full">
          <div className="max-w-2xl animate-in slide-in-from-left duration-1000">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-wider mb-6">
              <Zap className="h-3 w-3 fill-orange-700" />
              La máxima tecnología para el fitness
            </div>
            <h1 className="text-4xl xs:text-5xl md:text-7xl lg:text-8xl font-black text-slate-900 leading-[0.9] tracking-tighter mb-8 break-words">
              TU NEGOCIO <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">
                A UN CLICK
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-950 leading-relaxed max-w-lg mb-10 font-black">
              Gestiona tu gimnasio con la plataforma más rápida y profesional.
              Membresías, QR y analíticas diseñadas para resultados reales.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                variant="default"
                className="h-16 px-10 text-lg font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-2xl shadow-xl shadow-orange-600/30 transition-all hover:-translate-y-1"
                onClick={() => navigate('/register-gym')}
              >
                EMPIEZA TU PRUEBA GRATIS
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-16 px-10 text-lg font-bold border-slate-200 bg-white/50 backdrop-blur-sm hover:bg-slate-50 rounded-2xl transition-all"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                VER FUNCIONES
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-50 px-3 py-1.5 rounded-full">
                <CheckCircle2 className="h-3.5 w-3.5" /> 14 días gratis
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
                <CheckCircle2 className="h-3.5 w-3.5" /> Sin tarjeta de crédito
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full">
                <CheckCircle2 className="h-3.5 w-3.5" /> Configuración en 5 min
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-full">
                <CheckCircle2 className="h-3.5 w-3.5" /> Soporte local
              </span>
            </div>

            {/* Stats Summary */}
            <div className="mt-8 flex items-center gap-12 border-t border-slate-200 pt-8 hidden sm:flex">
              <div>
                <p className="text-3xl font-black text-slate-900">100%</p>
                <p className="text-xs font-bold text-slate-500 uppercase">Automatizado</p>
              </div>
              <div>
                <p className="text-3xl font-black text-slate-900">Ágil</p>
                <p className="text-xs font-bold text-slate-500 uppercase">Soporte Técnico</p>
              </div>
              <div>
                <p className="text-3xl font-black text-slate-900">QR</p>
                <p className="text-xs font-bold text-slate-500 uppercase">Acceso Rápido</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Module Spotlight Carousel */}
      <section id="experience" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-sm font-bold text-orange-600 uppercase tracking-widest mb-4">Experiencia Premium</h2>
            <p className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight">
              Diseñado para el <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-500 text-orange-600">mundo real</span> de los gimnasios.
            </p>
          </div>

          <ModuleCarousel />
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-sm font-bold text-orange-600 uppercase tracking-widest mb-4">Potencia tu negocio</h2>
            <p className="text-4xl font-extrabold text-slate-900 tracking-tight">Todo lo que necesitas para administrar tu gimnasio.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: ShieldCheck,
                title: "Seguridad SaaS",
                desc: "Datos protegidos y aislados. Privacidad total para la información de tu negocio.",
                color: "bg-blue-600"
              },
              {
                icon: Smartphone,
                title: "Mobile First",
                desc: "Panel optimizado para celulares. Gestiona desde la sala de pesas o desde casa.",
                color: "bg-orange-600"
              },
              {
                icon: Zap,
                title: "Configuración Rápida",
                desc: "Empieza en minutos. Carga tus planes y empieza a cobrar de inmediato.",
                color: "bg-amber-500"
              },
              {
                icon: Sparkles,
                title: "Analítica Predictiva",
                desc: "Entiende el comportamiento de tus socios y mejora la retención.",
                color: "bg-indigo-600"
              }
            ].map((f, i) => (
              <div key={i} className="group p-8 rounded-3xl bg-white border border-slate-100 hover:border-orange-200 transition-all hover:shadow-2xl hover:shadow-orange-100">
                <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center mb-6 text-white shadow-lg", f.color)}>
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-3">{f.title}</h3>
                <p className="text-slate-950 leading-relaxed text-sm font-black">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-12 max-w-5xl mx-auto text-center md:text-left">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-orange-600/10 blur-[60px] rounded-full -z-10" />
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-white shadow-2xl">
                <img
                  src="/benjamin-founder.png"
                  alt="Benjamin Garcia - Fundador de GymPro SV"
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-sm font-bold text-orange-600 uppercase tracking-widest mb-3 text-center md:text-left">Sobre Nosotros</h2>
                <h3 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight">
                  Pasión por la <span className="text-orange-600">Innovación</span> y el Servicio de Calidad.
                </h3>
              </div>

              <div className="text-lg text-slate-900 leading-relaxed font-bold italic">
                <p>
                  "Como fundador de <span className="text-slate-900 font-extrabold uppercase tracking-tight">GymPro SV</span>, mi mayor motivación es ver cómo nuestra tecnología impulsa el crecimiento de cada gimnasio que confía en nosotros.
                  Me entusiasma profundamente brindar una herramienta de alta calidad que facilite el día a día de nuestros clientes. Mi compromiso es firme: seguiremos innovando sin descanso para ofrecer siempre el mejor servicio y las soluciones más avanzadas del mercado.
                  <span className="text-slate-900 font-black italic not-italic ml-2">— Benjamin Garcia, Fundador & Lead Dev</span>"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Releases / Novedades */}
      <section id="releases" className="py-24 bg-slate-50 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-sm font-bold text-orange-600 uppercase tracking-widest mb-4">Novedades &amp; Releases</h2>
            <p className="text-4xl font-extrabold text-slate-900 tracking-tight">
              Mira lo ultimo que hemos lanzado en GymPro SV.
            </p>
          </div>

          <div className="max-w-3xl mx-auto relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200 hidden md:block" />

            <div className="space-y-10">
              {releases.map((release, i) => {
                const daysAgo = Math.floor((Date.now() - new Date(release.date).getTime()) / (1000 * 60 * 60 * 24));
                const relativeDate = daysAgo === 0 ? 'Hoy' : daysAgo === 1 ? 'Ayer' : daysAgo < 7 ? `Hace ${daysAgo} dias` : daysAgo < 30 ? `Hace ${Math.floor(daysAgo / 7)} semanas` : `Hace ${Math.floor(daysAgo / 30)} meses`;

                return (
                  <div key={i} className="md:pl-14 relative group">
                    {/* Timeline dot */}
                    <div className="absolute left-6 top-2 -translate-x-1/2 w-3 h-3 rounded-full bg-orange-600 ring-4 ring-orange-100 hidden md:block group-hover:scale-125 transition-transform" />

                    <div className="p-6 md:p-8 rounded-3xl bg-white border border-slate-100 hover:border-orange-200 hover:shadow-2xl hover:shadow-orange-100/50 transition-all">
                      {/* Header */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-orange-600 to-amber-500 text-white text-xs font-bold">
                          {release.version}
                        </span>
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                          {relativeDate}
                        </span>
                      </div>

                      <h3 className="text-xl font-black text-slate-900 mb-4">{release.title}</h3>

                      {/* Changes list */}
                      <ul className="space-y-3">
                        {release.changes.map((change, j) => (
                          <li key={j} className="flex items-start gap-3">
                            {change.type === 'feature' && (
                              <div className="shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                                <Sparkles className="h-3 w-3 text-purple-600" />
                              </div>
                            )}
                            {change.type === 'improvement' && (
                              <div className="shrink-0 h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
                                <Zap className="h-3 w-3 text-amber-600 fill-amber-600" />
                              </div>
                            )}
                            {change.type === 'fix' && (
                              <div className="shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                                <CheckCircle2 className="h-3 w-3 text-green-600" />
                              </div>
                            )}
                            <span className="text-sm text-slate-700 font-medium leading-relaxed">{change.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="bg-slate-900 rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-orange-600/20 blur-[100px] rounded-full" />
            <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-64 h-64 bg-amber-500/20 blur-[100px] rounded-full" />

            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-4xl md:text-6xl font-black text-white mb-6">¿LISTO PARA EL SIGUIENTE NIVEL?</h2>
              <p className="text-slate-400 text-lg mb-10">Únete a la plataforma que está transformando los gimnasios en la región.</p>
              <Button
                size="lg"
                variant="default"
                className="bg-white text-slate-900 hover:bg-slate-100 h-16 px-12 text-xl font-bold rounded-2xl transition-all hover:scale-105"
                onClick={() => navigate('/register-gym')}
              >
                EMPIEZA TU PRUEBA GRATIS
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Widget */}
      <WhatsAppWidget />

      <footer className="py-12 border-t border-slate-100 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-12">
            <div className="flex items-center gap-2 group">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-600 shadow-lg shadow-orange-600/20">
                <Dumbbell className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">GymPro <span className="text-orange-600">SV</span></span>
            </div>

            <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
              <a href="/legal/terms" className="text-sm font-black text-slate-800 hover:text-orange-600 transition-colors">Términos de Servicio</a>
              <a href="/legal/privacy" className="text-sm font-black text-slate-800 hover:text-orange-600 transition-colors">Privacidad</a>
              <a href="/legal/legal" className="text-sm font-black text-slate-800 hover:text-orange-600 transition-colors">Aviso Legal</a>
              <a href="/legal/disclaimer" className="text-sm font-black text-slate-800 hover:text-orange-600 transition-colors">Disclaimer</a>
            </div>
          </div>

          <div className="text-center pt-8 border-t border-slate-200/60">
            <p className="text-slate-500 text-sm font-medium mb-2">
              © {new Date().getFullYear()} GymPro SV. Todos los derechos reservados.
            </p>
            <p className="text-slate-400 text-[10px] uppercase font-bold tracking-[0.2em]">
              Hecho con ❤️ en El Salvador
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
